import { useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/base/ScrollToTop";
import SEOHead from "@/components/base/SEOHead";
import Breadcrumb from "@/components/base/Breadcrumb";
import ArticleCard from "@/components/base/ArticleCard";
import { categories } from "@/mocks/categories";
import { articles } from "@/mocks/articles";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug);
  const [activeSlug, setActiveSlug] = useState(slug || "tools");

  const filteredArticles = useMemo(
    () => articles.filter((a) => a.categoryId === currentCategory?.id),
    [currentCategory]
  );

  const handleCategoryChange = (newSlug: string) => {
    setActiveSlug(newSlug);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (!currentCategory) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <div className="min-h-screen bg-background-50 pt-24 pb-20">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 text-center">
            <h1 className="text-2xl font-bold text-foreground-900 mb-4">分类未找到</h1>
            <Link
              to="/"
              className="text-primary-500 hover:text-primary-600 transition-colors duration-200 cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const categorySchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${currentCategory.name} - 91精选`,
    description: currentCategory.description,
    url: `https://91jingxuan.com/category/${currentCategory.slug}`,
    mainEntity: {
      "@type": "ItemList",
      itemListElement: filteredArticles.map((article, index) => ({
        "@type": "ListItem",
        position: index + 1,
        url: `https://91jingxuan.com/article/${article.id}`,
        name: article.title,
      })),
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${currentCategory.name} - 91精选精品内容聚合`}
        description={`${currentCategory.description}。浏览91精选${currentCategory.name}分类下的所有精选文章，发现最值得收藏的${currentCategory.name}类优质资源。`}
        keywords={`91精选${currentCategory.name},${currentCategory.name}推荐,精选${currentCategory.name},${currentCategory.name}资源`}
        canonicalUrl={`/category/${currentCategory.slug}`}
        schema={categorySchema}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-24 pb-20">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                { label: currentCategory.name },
              ]}
            />

            <div className="mt-6 mb-10">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground-900 mb-3">
                {currentCategory.name}
              </h1>
              <p className="text-foreground-600 text-sm md:text-base">
                {currentCategory.description}
              </p>
            </div>

            <div className="flex flex-wrap gap-2 mb-10">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  onClick={(e) => {
                    e.preventDefault();
                    handleCategoryChange(cat.slug);
                  }}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm transition-all duration-300 cursor-pointer whitespace-nowrap ${
                    cat.slug === activeSlug
                      ? "bg-primary-500 text-background-50 font-medium"
                      : "bg-background-100 text-foreground-700 hover:bg-background-200/70 border border-background-200/70"
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-600">该分类下暂无文章</p>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}